import React, { useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Settings2, ClipboardCheck, Wand2, Layers, Info, Plus, Trash2, Copy,
  FileDown, FileUp,
} from "lucide-react";
import { toast } from "sonner";
import {
  ORDERING_CATALOG,
  specForUnitModel,
  defaultsFor,
  parseNozzleLabel,
} from "../../data/orderingCodes";
import {
  buildFullCode, buildBaseCode, buildAccessoryCode,
} from "../../lib/orderingCodeBuilder";
import { buildUnitSchedule } from "../../lib/unitSchedule";
import { withVariantTags } from "../../lib/variantTags";
import {
  buildConfigTemplate, parseConfigTemplate, downloadBlob,
} from "../../lib/configTemplate";

// ----------------------------------------------------------------------------
// Configurator — DEFINES variants only. Room→variant assignment moves to a
// dedicated "Assignment" tab that renders next.
//
// Each variant gets an auto-generated tag like "EWS-1, EWS-2, FACB-1…" based
// on its group's model family.
// ----------------------------------------------------------------------------

const newId = () => `cfg_${Math.random().toString(36).slice(2, 9)}_${Date.now()}`;

// circuits distinguishes variants at the same (model, length) that differ only
// in engine-selected circuit count (H3 rule emits both 1-cct and 2-cct rows).
//
// Key format (4 parts): unit_model__specId__lengthFt__circuits
//   unit_model is the primary discriminator — it distinguishes Model C from
//   Model C1 even though both share spec id "C". spec.id is kept for
//   diagnostics. Must stay in sync with variantTags.keyForGroup and
//   configurationSchedule.buildBomRows.
function groupKey(unitModel, lengthFt, circuits = null) {
  const spec = specForUnitModel(unitModel);
  return `${unitModel}__${spec?.id || ""}__${lengthFt ?? ""}__${circuits ?? ""}`;
}

// Backward-compat: projects saved before this change stored cfg.key in the
// old 3-part format: "specId__lengthFt__circuits" (e.g. "C__4__1").
// When looking up a group from a saved cfg.key, also try the old shape.
function legacyKeyForGroup(g) {
  const spec = specForUnitModel(g.unit_model);
  return `${spec?.id || g.unit_model}__${g.length_ft ?? ""}__${g.circuits ?? ""}`;
}

function defaultExtras() {
  return {
    enclosure_color_ral: "9075",
    grille_color_ral: "9075",
    color_ral: "9075",
  };
}

function compactValues(spec, cfg, group) {
  const fields = group === "base" ? spec.baseFields : spec.accessoryFields;
  const cur = cfg[group] || {};
  const next = { ...cur };
  let changed = false;
  fields.forEach((f) => {
    if (typeof f.visibleWhen === "function" && !f.visibleWhen(cfg)) {
      if (next[f.key] !== f.default) { next[f.key] = f.default; changed = true; }
    }
  });
  return changed ? next : cur;
}
function compactAll(spec, cfg) {
  const base = compactValues(spec, cfg, "base");
  const accessory = compactValues(spec, { ...cfg, base }, "accessory");
  if (base === cfg.base && accessory === cfg.accessory) return cfg;
  return { ...cfg, base, accessory };
}

// Seed a fresh variant. Assignments stay empty here — they're filled on the
// Assignment tab.
function seedConfig(group, name = "Default", projectAdvanced = null) {
  const spec = specForUnitModel(group.unit_model);
  if (!spec) {
    return {
      id: newId(), key: groupKey(group.unit_model, group.length_ft, group.circuits),
      model_id: null, name, assignments: [],
      base: {}, accessory: {}, extras: defaultExtras(),
    };
  }
  const d = defaultsFor(spec);
  const lenField = spec.baseFields.find((f) => f.key === "length");
  const len = group.length_ft;
  if (lenField && len != null && lenField.options.some((o) => o.value === len)) {
    d.base.length = len;
  }
  // Model O-2 — pre-fill `coil_connection` from the selection engine's
  // pick (project.advanced.o2_circuit = "serial" | "parallel"). The
  // engineer can still override in the Configurator.
  const isO2 = group.unit_model === "Model O-2";
  if (isO2 && spec.baseFields.some((f) => f.key === "coil_connection")) {
    const circuit = projectAdvanced?.o2_circuit;
    if (circuit === "serial") d.base.coil_connection = 1;
    else if (circuit === "parallel") d.base.coil_connection = 2;
  }
  // Seed engine-sourced auto fields so the ordering code has a concrete value
  // even before the sync effect has run.
  if (spec.baseFields.some((f) => f.key === "coil_circuits") && group.circuits != null) {
    d.base.coil_circuits = group.circuits;
  }
  if (spec.baseFields.some((f) => f.key === "no_coil_pipes")) {
    // Default to 2 (matches engine + UI radio default); overridden at build
    // time by orderingCodeBuilder from project.advanced.no_coil_pipes, so this
    // value is only used as a display placeholder in the auto-fields section.
    d.base.no_coil_pipes = projectAdvanced?.no_coil_pipes ?? 2;
  }
  // Nozzle auto-population for PA-induction models (I / L / H / C).
  // The selection engine stamps the picked nozzle as e.g. "A-22" on the
  // group's per_unit data; parse it into the two base fields the
  // spreadsheet exposes (`nozzle_type` letter + `num_nozzles` count).
  const hasNozzleFields =
    spec.baseFields.some((f) => f.key === "nozzle_type") &&
    spec.baseFields.some((f) => f.key === "num_nozzles");
  if (hasNozzleFields) {
    const nz = parseNozzleLabel(group?.per_unit?.nozzle_label);
    if (nz) {
      d.base.nozzle_type = nz.type;
      d.base.num_nozzles = nz.count;
    }
  }
  return {
    id: newId(), key: groupKey(group.unit_model, group.length_ft, group.circuits),
    // Preserve the room's actual unit_model so encoder picks the right
    // prefix (S vs S-HW, O-1 vs O-2) when the catalog is shared.
    unit_model: group.unit_model,
    // Carry the engine-selected circuit count so buildBaseCode can emit the
    // correct coil_circuits digit without digging through the group separately.
    circuits: group.circuits ?? null,
    model_id: spec.id, name, assignments: [],
    base: d.base, accessory: d.accessory,
    // Per-accessory install mode override ("factory" | "box"). Initialised
    // from the spec's `note` so existing variants render the spec default
    // out of the box; the engineer can override per field in the UI.
    accessory_install: d.accessoryInstall,
    extras: defaultExtras(),
  };
}

// Detect variants whose base/accessory keys don't match the current spec.
// Per user direction (2026-05-21), on a spec rewrite we WIPE & RESET stale
// variants to defaults rather than try to migrate moved fields piecemeal.
function isStaleAgainstSpec(cfg, spec) {
  if (!spec) return false;
  const specBaseKeys = new Set(spec.baseFields.map((f) => f.key));
  const specAccKeys = new Set(spec.accessoryFields.map((f) => f.key));
  const cfgBaseKeys = Object.keys(cfg.base || {});
  const cfgAccKeys = Object.keys(cfg.accessory || {});
  // Any base/accessory key that's no longer in the spec → stale.
  for (const k of cfgBaseKeys) if (!specBaseKeys.has(k)) return true;
  for (const k of cfgAccKeys) if (!specAccKeys.has(k)) return true;
  // Any required base field that's missing entirely → also stale.
  for (const f of spec.baseFields) {
    if (f.auto) continue;
    if (cfg.base?.[f.key] === undefined) return true;
  }
  return false;
}

// Migrate legacy variant {qty: N} → {assignments: []}; assignments populated
// later via the Assignment tab.
function migrateLegacyVariant(v) {
  if (Array.isArray(v.assignments)) return v;
  const { qty: _qty, ...rest } = v;
  return { ...rest, assignments: [] };
}

// Drop assignments referring to rooms no longer in the group.
// Prefer room_id matching (schema v3); fall back to room_idx for older payloads.
function pruneAssignments(v, group) {
  const validIds = new Set(group.rooms.map((r) => r.room_id).filter(Boolean));
  const validIdxs = new Set(group.rooms.map((r) => r.room_idx));
  const next = (v.assignments || []).filter((a) => {
    // If both sides have room_id, use it exclusively (schema v3).
    if (a.room_id != null && validIds.size > 0) return validIds.has(a.room_id);
    return validIdxs.has(a.room_idx); // backward-compat
  });
  if (next.length === (v.assignments?.length ?? 0)) return v;
  return { ...v, assignments: next };
}

// ----------------------------------------------------------------------------
// FieldInput
// ----------------------------------------------------------------------------
function FieldInput({ field, value, onChange, ralValue, onRalChange }) {
  if (field.type === "merv") {
    return (
      <Select value={String(value ?? field.default)} onValueChange={onChange}>
        <SelectTrigger data-testid={`cfg-field-${field.key}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => (
            <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <div className="space-y-1">
      <Select
        value={String(value ?? field.default)}
        onValueChange={(v) => {
          const num = Number(v);
          onChange(Number.isFinite(num) && String(num) === v ? num : v);
        }}
      >
        <SelectTrigger data-testid={`cfg-field-${field.key}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => (
            <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {field.type === "ral" && Number(value) === 3 && (
        <div className="flex items-center gap-2 pl-1">
          <span className="text-xs text-muted-foreground">Custom RAL:</span>
          <Input
            value={ralValue || ""}
            onChange={(e) => onRalChange((e.target.value || "").replace(/[^0-9]/g, "").slice(0, 4))}
            placeholder="9005"
            className="h-7 w-24 text-xs"
            data-testid={`cfg-field-${field.key}-ral`}
          />
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// VariantCard — displays the auto-tag prominently
// ----------------------------------------------------------------------------
function VariantCard({
  spec, cfg, variantIdx, totalVariants, onChange, onDelete, onDuplicate, onReset, dataIdx,
  project, unitModel,
}) {
  // Pass project (for no_coil_pipes) and cfg.circuits (engine-selected circuit
  // count) through to the builder so auto fields encode correctly.
  // unitModel comes from the parent's resolved group so spec-sharing models
  // (Model C vs C1, S vs S-HW, O-1 vs O-2, I-* / L-*) print their own
  // prefix in the code (e.g., "B-C1...") instead of falling back to the
  // shared spec.basePrefix ("B-C").
  const effectiveUnitModel = unitModel || cfg.unit_model;
  const fullCode = buildFullCode(spec, { ...cfg, unit_model: effectiveUnitModel, project, circuits: cfg.circuits });
  const baseCode = buildBaseCode(spec, cfg.base, cfg.extras, {
    unitModel: effectiveUnitModel,
    project,
    circuits: cfg.circuits,
  });
  const accCode = buildAccessoryCode(spec, cfg.accessory, cfg.extras);

  const isVisible = (f) => typeof f.visibleWhen !== "function" || f.visibleWhen(cfg);
  const editableBaseFields = spec.baseFields.filter(
    (f) => f.key !== "length" && !f.auto && isVisible(f)
  );
  const autoBaseFields = spec.baseFields.filter((f) => f.auto && isVisible(f));
  const editableAccessoryFields = spec.accessoryFields.filter(isVisible);

  const updateBase = (key, value) => {
    const next = { ...cfg, base: { ...cfg.base, [key]: value } };
    onChange(compactAll(spec, next));
  };
  const updateAccessory = (key, value) => {
    const next = { ...cfg, accessory: { ...cfg.accessory, [key]: value } };
    onChange(compactAll(spec, next));
  };
  const updateExtra = (key, value) =>
    onChange({ ...cfg, extras: { ...cfg.extras, [key]: value } });
  // Per-accessory FI/Box override. The catalog's `note` (Factory Installed /
  // Separate in a box) provides the default; the engineer can flip per
  // accessory via the small inline dropdown in the row label.
  const updateInstall = (key, mode) => {
    onChange({
      ...cfg,
      accessory_install: { ...(cfg.accessory_install || {}), [key]: mode },
    });
  };

  return (
    <div className="border border-border rounded-md bg-card" data-testid={`cfg-variant-${dataIdx}`}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-muted border-b border-border rounded-t-md">
        <span
          className="font-mono text-xs px-2 py-1 rounded bg-foreground text-background shrink-0"
          data-testid={`cfg-variant-tag-${dataIdx}`}
        >
          {cfg.tag}
        </span>
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
          Variant
        </span>
        <span className="text-xs font-mono text-muted-foreground">#{variantIdx + 1}</span>
        <Input
          value={cfg.name || ""}
          onChange={(e) => onChange({ ...cfg, name: e.target.value })}
          placeholder="e.g. Left piping"
          className="h-7 text-sm font-medium max-w-xs"
          data-testid={`cfg-name-${dataIdx}`}
        />
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onReset} title="Reset to standard"
            data-testid={`cfg-reset-${dataIdx}`}>
            <Wand2 className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDuplicate} title="Duplicate variant"
            data-testid={`cfg-dup-${dataIdx}`}>
            <Copy className="h-4 w-4" />
          </Button>
          {totalVariants > 1 && (
            <Button size="sm" variant="ghost" onClick={onDelete} title="Delete variant"
              className="text-rose-600 hover:text-rose-700"
              data-testid={`cfg-del-${dataIdx}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-5">
        {autoBaseFields.length > 0 && (
          <section
            className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/30 px-3 py-2"
            data-testid={`cfg-auto-fields-${dataIdx}`}
          >
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold mb-1.5">
              Auto from selection
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-emerald-900 dark:text-emerald-300 font-mono">
              {autoBaseFields.map((f) => {
                const raw = cfg.base?.[f.key];
                const display =
                  f.type === "select"
                    ? (f.options.find((o) => o.value === raw)?.label ?? String(raw ?? "—"))
                    : (raw != null && raw !== "" ? String(raw) : "—");
                return (
                  <div key={f.key}>
                    <span className="text-emerald-700/80 dark:text-emerald-400/80">{f.label}:</span>{" "}
                    <span className="font-semibold">{display}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section>
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
            <Settings2 className="h-3.5 w-3.5" /> Base Unit Options
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {editableBaseFields.map((f) => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs text-muted-foreground">{f.label}</Label>
                <FieldInput
                  field={f}
                  value={cfg.base[f.key]}
                  onChange={(v) => updateBase(f.key, v)}
                  ralValue={cfg.extras?.[f.key + "_ral"]}
                  onRalChange={(v) => updateExtra(f.key + "_ral", v)}
                />
              </div>
            ))}
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
            <ClipboardCheck className="h-3.5 w-3.5" /> Accessories &amp; Sensors
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {editableAccessoryFields.map((f) => {
              const defaultInstall =
                f.note === "Factory Installed" ? "factory"
                  : f.note === "Separate in a box" ? "box"
                  : null;
              const install = defaultInstall
                ? (cfg.accessory_install?.[f.key] || defaultInstall)
                : null;
              return (
                <div key={f.key} className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center justify-between gap-2">
                    <span className="truncate" title={f.label}>{f.label}</span>
                    {install != null && (
                      <select
                        value={install}
                        onChange={(e) => updateInstall(f.key, e.target.value)}
                        className={`text-[10px] uppercase font-mono ml-2 px-1.5 py-0.5 rounded border outline-none focus:ring-1 focus:ring-border ${
                          install === "factory"
                            ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800"
                            : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800"
                        }`}
                        title="Where this accessory is supplied"
                        data-testid={`cfg-install-${dataIdx}-${f.key}`}
                      >
                        <option value="factory">FAC</option>
                        <option value="box">BOX</option>
                      </select>
                    )}
                  </Label>
                  <FieldInput
                    field={f}
                    value={cfg.accessory[f.key]}
                    onChange={(v) => updateAccessory(f.key, v)}
                    ralValue={cfg.extras?.[f.key + "_ral"]}
                    onRalChange={(v) => updateExtra(f.key + "_ral", v)}
                  />
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-card border border-border rounded-lg p-4 -mx-2" data-testid={`cfg-output-${dataIdx}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Ordering code · {cfg.tag}
            </div>
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => {
                navigator.clipboard.writeText(fullCode).then(
                  () => toast.success("Ordering code copied"),
                  () => toast.error("Copy failed")
                );
              }}
              data-testid={`cfg-copy-${dataIdx}`}
            >Copy</Button>
          </div>
          <div className="font-mono text-sm break-all leading-relaxed">
            <span className="text-orange-600 dark:text-orange-400">{baseCode}</span>
            <span className="text-muted-foreground/40 mx-2">|</span>
            <span className="text-teal-600 dark:text-teal-400">{accCode}</span>
          </div>
        </section>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// GroupCard — variants for one (model, length); no matrix here.
// ----------------------------------------------------------------------------
function GroupCard({ group, variants, idxBase, onUpdateAt, onDeleteAt, onDuplicateAt, onResetAt, onAddVariant, isSI = false, project = null }) {
  const spec = useMemo(() => specForUnitModel(group.unit_model), [group.unit_model]);
  // Model N / N-f have a single physical size — don't display a length next to
  // their name in the configurator header.
  const isLengthAgnostic = group.unit_model === "Model N" || group.unit_model === "Model N-f";
  const lenLabel = !isLengthAgnostic && group.length_ft != null
    ? (isSI ? `${Math.round(parseFloat(group.length_ft) * 300)} mm` : `${group.length_ft} ft`)
    : "";

  if (!spec) {
    return (
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/30">
        <CardContent className="py-4 text-sm text-amber-800 dark:text-amber-300">
          <Info className="h-4 w-4 inline mr-2" />
          No ordering spec available for <strong>{group.unit_model}</strong>.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border" data-testid={`cfg-group-${group.unit_model}-${group.length_ft || 0}`}>
      <CardHeader className="pb-3 bg-muted/50 border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-base text-foreground">
              {group.unit_model}
              {lenLabel && (
                <>
                  {" "}<span className="text-muted-foreground font-normal">·</span>{" "}
                  <span className="text-muted-foreground font-mono text-sm">{lenLabel}</span>
                </>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Define one or more configurations for this unit type.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onAddVariant}
            data-testid={`cfg-add-variant-${group.unit_model}-${group.length_ft || 0}`}>
            <Plus className="h-4 w-4 mr-1" /> Variant
          </Button>
        </div>
      </CardHeader>

      <CardContent className="py-4 space-y-3">
        {variants.map((v, i) => (
          <VariantCard
            key={v.id}
            spec={spec}
            cfg={v}
            variantIdx={i}
            totalVariants={variants.length}
            dataIdx={idxBase + i}
            project={project}
            unitModel={group.unit_model}
            onChange={(next) => onUpdateAt(idxBase + i, next)}
            onDelete={() => onDeleteAt(idxBase + i)}
            onDuplicate={() => onDuplicateAt(idxBase + i)}
            onReset={() => onResetAt(idxBase + i)}
          />
        ))}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Main tab
// ----------------------------------------------------------------------------
export default function ConfiguratorTab({
  configurations, setConfigurations, rooms, results, isSI = false, project = null,
}) {
  const fileInputRef = React.useRef(null);
  const groups = useMemo(
    () => buildUnitSchedule(rooms || [], results || []),
    [rooms, results]
  );

  // Sync configurations to current groups: ensure each group has ≥1 variant;
  // prune variants whose group is gone; migrate legacy {qty} → {assignments:[]}
  useEffect(() => {
    const cfgs = Array.isArray(configurations) ? configurations : [];
    const groupByKey = new Map();
    // Legacy-key fallback: keeps old saved projects working when groupKey
    // format changes. Three generations of key format exist in the wild:
    //   v20.7-: "specId__length"           (2 parts, no circuits)
    //   v20.8:  "specId__length__circuits"  (3 parts — now the OLD format)
    //   v20.9+: "unitModel__specId__length__circuits" (4 parts — NEW, fixes C/C1 collision)
    // Index every group by ALL three key shapes so any saved project migrates
    // transparently; cfg.key is re-written to the new format on first load.
    const groupByLegacyKey = new Map();
    groups.forEach((g) => {
      const newKey = groupKey(g.unit_model, g.length_ft, g.circuits);
      groupByKey.set(newKey, g);
      const specId = specForUnitModel(g.unit_model)?.id || g.unit_model;
      // v20.8 key (3 parts): "specId__length__circuits"
      const v208Key = `${specId}__${g.length_ft ?? ""}__${g.circuits ?? ""}`;
      // v20.7 key (2 parts): "specId__length"  — circuits absent
      const v207Key = `${specId}__${g.length_ft ?? ""}`;
      // When two groups share the same legacy key (e.g. Model C and Model C1
      // both produce "C__4__1"), first-write wins — the ambiguous old save
      // lands on one group; the user can re-assign in the Configurator.
      if (!groupByLegacyKey.has(v208Key)) {
        groupByLegacyKey.set(v208Key, { group: g, newKey });
      }
      if (!groupByLegacyKey.has(v207Key)) {
        groupByLegacyKey.set(v207Key, { group: g, newKey });
      }
    });
    const byKey = new Map();
    cfgs.forEach((c) => {
      let g = groupByKey.get(c.key);
      let resolvedKey = c.key;
      if (!g) {
        const legacy = groupByLegacyKey.get(c.key);
        if (legacy) {
          g = legacy.group;
          resolvedKey = legacy.newKey;
          // Mutate the local config copy so downstream code (byKey,
          // mutation detection) sees the new key. The original cfgs[i]
          // is untouched until setConfigurations(next) runs below.
          c = { ...c, key: resolvedKey };
        }
      }
      if (!g) return;
      const spec = specForUnitModel(g.unit_model);
      let migrated = pruneAssignments(migrateLegacyVariant(c), g);
      // Wipe & reset stale variants (whose keys don't match the current
      // spec). Preserve id / name / assignments so the user's room mapping
      // isn't lost.
      if (spec && isStaleAgainstSpec(migrated, spec)) {
        const seeded = seedConfig(g, migrated.name || "Default", project?.advanced);
        seeded.id = migrated.id;
        seeded.assignments = migrated.assignments || [];
        migrated = seeded;
      }
      // Keep auto-populated engine fields fresh — driven by selection engine,
      // so any time the group's pick changes the variant must reflect it.
      if (spec && spec.baseFields.some((f) => f.auto)) {
        const nz = parseNozzleLabel(g.per_unit?.nozzle_label);
        const freshCircuits = g.circuits ?? null;
        const freshPipes = project?.advanced?.no_coil_pipes ?? 2;
        const needsNozzle = nz && (
          migrated.base?.nozzle_type !== nz.type ||
          migrated.base?.num_nozzles !== nz.count
        );
        const needsCircuits = spec.baseFields.some((f) => f.key === "coil_circuits") &&
          migrated.base?.coil_circuits !== freshCircuits && freshCircuits != null;
        const needsPipes = spec.baseFields.some((f) => f.key === "no_coil_pipes") &&
          migrated.base?.no_coil_pipes !== freshPipes;
        if (needsNozzle || needsCircuits || needsPipes) {
          migrated = {
            ...migrated,
            circuits: freshCircuits,
            base: {
              ...migrated.base,
              ...(nz ? { nozzle_type: nz.type, num_nozzles: nz.count } : {}),
              ...(needsCircuits ? { coil_circuits: freshCircuits } : {}),
              ...(needsPipes ? { no_coil_pipes: freshPipes } : {}),
            },
          };
        }
      }
      if (!byKey.has(c.key)) byKey.set(c.key, []);
      byKey.get(c.key).push(migrated);
    });
    const next = [];
    let mutated = false;
    groups.forEach((g) => {
      const k = groupKey(g.unit_model, g.length_ft, g.circuits);
      const existing = byKey.get(k);
      if (existing && existing.length > 0) next.push(...existing);
      else { next.push(seedConfig(g, "Default", project?.advanced)); mutated = true; }
    });
    if (next.length !== cfgs.length) mutated = true;
    else {
      for (let i = 0; i < cfgs.length; i++) {
        if (cfgs[i] !== next[i] && JSON.stringify(cfgs[i]) !== JSON.stringify(next[i])) {
          mutated = true; break;
        }
      }
    }
    if (mutated) setConfigurations(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Annotate every variant with its auto tag (EWS-1, EWS-2, FACB-1…)
  const tagged = useMemo(
    () => withVariantTags(configurations || [], groups),
    [configurations, groups]
  );

  const variantsByKey = useMemo(() => {
    const m = new Map();
    tagged.forEach((c) => {
      if (!m.has(c.key)) m.set(c.key, []);
      m.get(c.key).push(c);
    });
    return m;
  }, [tagged]);

  // updateAt operates on the underlying configurations[] (untagged). Find the
  // matching variant by id and write back the new shape (without the tag).
  const updateAt = useCallback((idx, next) => {
    const cfgs = Array.isArray(configurations) ? configurations : [];
    const { tag: _t, ...rest } = next;
    setConfigurations(cfgs.map((c, i) => (i === idx ? rest : c)));
  }, [configurations, setConfigurations]);

  const deleteAt = useCallback((idx) => {
    const cfgs = Array.isArray(configurations) ? configurations : [];
    if (cfgs.length <= 1) return;
    setConfigurations(cfgs.filter((_, i) => i !== idx));
  }, [configurations, setConfigurations]);

  const duplicateAt = useCallback((idx) => {
    const cfgs = Array.isArray(configurations) ? configurations : [];
    const orig = cfgs[idx];
    if (!orig) return;
    const copy = {
      ...orig,
      id: newId(),
      name: `${orig.name || "Variant"} (copy)`,
      assignments: [],
      base: { ...orig.base },
      accessory: { ...orig.accessory },
      extras: { ...orig.extras },
    };
    const next = [...cfgs];
    next.splice(idx + 1, 0, copy);
    setConfigurations(next);
    toast.success("Variant duplicated");
  }, [configurations, setConfigurations]);

  const resetAt = useCallback((idx) => {
    const cfgs = Array.isArray(configurations) ? configurations : [];
    const orig = cfgs[idx];
    if (!orig) return;
    const g = groups.find((x) => groupKey(x.unit_model, x.length_ft, x.circuits) === orig.key);
    if (!g) return;
    const seeded = seedConfig(g, orig.name || "Default", project?.advanced);
    seeded.id = orig.id;
    seeded.assignments = orig.assignments;
    updateAt(idx, seeded);
    toast.success("Reset to standard configuration");
  }, [configurations, groups, updateAt]);

  const addVariant = useCallback((groupKeyStr) => {
    const cfgs = Array.isArray(configurations) ? configurations : [];
    const g = groups.find((x) => groupKey(x.unit_model, x.length_ft, x.circuits) === groupKeyStr);
    if (!g) return;
    const existing = (variantsByKey.get(groupKeyStr) || []).length;
    let insertAt = cfgs.length;
    for (let i = cfgs.length - 1; i >= 0; i--) {
      if (cfgs[i].key === groupKeyStr) { insertAt = i + 1; break; }
    }
    const seeded = seedConfig(g, `Variant ${existing + 1}`, project?.advanced);
    const next = [...cfgs];
    next.splice(insertAt, 0, seeded);
    setConfigurations(next);
    toast.success("Variant added — assign rooms in the next tab");
  }, [groups, configurations, variantsByKey, setConfigurations]);

  const groupedRender = useMemo(() => {
    const result = [];
    let cursor = 0;
    groups.forEach((g) => {
      const k = groupKey(g.unit_model, g.length_ft, g.circuits);
      const variants = variantsByKey.get(k) || [];
      // React key must be unique even when shared catalogs (e.g. S/S-HW or
      // O-1/O-2) produce the same `groupKey`, or when two circuit variants
      // exist for the same (model, length). Suffix with unit_model + circuits.
      const renderKey = `${g.unit_model}__${g.length_ft ?? ""}__${g.circuits ?? ""}`;
      result.push({ group: g, variants, idxBase: cursor, key: k, renderKey });
      cursor += variants.length;
    });
    return result;
  }, [groups, variantsByKey]);

  const totalVariants = tagged.length;

  // ----------------------------------------------------------------------
  // Config Template — export + import
  // ----------------------------------------------------------------------
  const handleExportTemplate = useCallback(async () => {
    if (!groups.length) {
      toast.error("No selections yet — finish the Summary tab first.");
      return;
    }
    try {
      const blob = await buildConfigTemplate(project || {}, tagged, groups);
      const safeName = (project?.name || "project")
        .replace(/[^A-Za-z0-9_-]+/g, "_")
        .slice(0, 60);
      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `ZAMBA_${safeName}_ConfigTemplate_${today}.xlsx`);
      toast.success("Config template downloaded");
    } catch (e) {
      console.error("Config template export failed", e);
      toast.error("Failed to build template — check console.");
    }
  }, [project, tagged, groups]);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImportFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!groups.length) {
      toast.error("No selections yet — finish the Summary tab first.");
      return;
    }
    try {
      const buf = await file.arrayBuffer();
      const { variants, warnings } = await parseConfigTemplate(buf, groups);
      if (!variants.length) {
        toast.error("No valid variants found in the file.");
        return;
      }

      // Replace strategy: for each (model, length) group present in the
      // imported set, drop existing variants for that group and substitute
      // the imported ones. Other groups are left untouched.
      const cfgs = Array.isArray(configurations) ? configurations : [];
      const touchedKeys = new Set(variants.map((v) => v.key));
      const kept = cfgs.filter((c) => !touchedKeys.has(c.key));
      // Re-populate auto fields (nozzle_type, num_nozzles) on imported
      // variants from the matching group's per_unit data so the imported
      // ordering code prints the correct nozzle.
      const groupByKey = new Map();
      groups.forEach((g) => groupByKey.set(groupKey(g.unit_model, g.length_ft, g.circuits), g));
      const enriched = variants.map((v) => {
        const g = groupByKey.get(v.key);
        if (!g) return v;
        const nz = parseNozzleLabel(g.per_unit?.nozzle_label);
        if (!nz) return v;
        return { ...v, base: { ...v.base, nozzle_type: nz.type, num_nozzles: nz.count } };
      });
      setConfigurations([...kept, ...enriched]);

      const msg = `Imported ${variants.length} variant${variants.length === 1 ? "" : "s"} across ${touchedKeys.size} group${touchedKeys.size === 1 ? "" : "s"}.`;
      if (warnings.length) {
        console.warn("Config import warnings:", warnings);
        toast.success(`${msg}  ${warnings.length} warning${warnings.length === 1 ? "" : "s"} — see console.`);
      } else {
        toast.success(msg);
      }
    } catch (err) {
      console.error("Config template import failed", err);
      toast.error("Could not read the file. Make sure it's an unmodified ZAMBA template.");
    }
  }, [groups, configurations, setConfigurations]);

  return (
    <div className="space-y-4" data-testid="configurator-tab">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleImportFile}
        className="hidden"
        data-testid="cfg-import-input"
      />
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="h-4 w-4" /> Define Configurations
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Build the variants you need (e.g. <em>EWS-1</em>, <em>EWS-2</em>, <em>EWS-3</em>).
                Each variant produces its own ordering code. Click <strong>Next</strong> when done to
                assign rooms to configurations.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={handleExportTemplate}
                disabled={!groups.length}
                data-testid="cfg-export-template"
                title="Download an Excel template with dropdowns for the client to fill in"
              >
                <FileDown className="h-4 w-4 mr-1" /> Config Template
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleImportClick}
                disabled={!groups.length}
                data-testid="cfg-import-template"
                title="Import a completed config workbook to rebuild variants automatically"
              >
                <FileUp className="h-4 w-4 mr-1" /> Import Config
              </Button>
              {groups.length > 0 && (
                <div className="text-right text-xs text-muted-foreground pl-2 border-l border-border" data-testid="cfg-summary">
                  <div>
                    <span className="font-mono text-foreground font-semibold">{groups.length}</span> group(s) ·{" "}
                    <span className="font-mono text-foreground font-semibold">{totalVariants}</span> variant(s)
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {groups.length === 0 && (
        <div className="border-2 border-dashed border-border rounded-lg p-12 text-center" data-testid="cfg-empty">
          <Settings2 className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-foreground mb-1 font-medium">No selections yet</p>
          <p className="text-xs text-muted-foreground">
            Go back to <strong>Advanced → Perform Selection</strong> first.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {groupedRender.map(({ group, variants, idxBase, key, renderKey }) => (
          <GroupCard
            key={renderKey}
            group={group}
            variants={variants}
            idxBase={idxBase}
            onUpdateAt={updateAt}
            onDeleteAt={deleteAt}
            onDuplicateAt={duplicateAt}
            onResetAt={resetAt}
            onAddVariant={() => addVariant(key)}
            isSI={isSI}
            project={project}
          />
        ))}
      </div>
    </div>
  );
}
